import { useSearchParams } from 'react-router-dom';
import type { AuthUser } from '@/lib/api';
import { cn } from '@/lib/utils';
import WeeklyTab from '@/components/insights/WeeklyTab';
import ReportingTab from '@/components/insights/ReportingTab';
import GrowthTab from '@/components/insights/GrowthTab';
import TroopMixTab from '@/components/insights/TroopMixTab';
import VsPowerTab from '@/components/insights/VsPowerTab';
import CombatTab from '@/components/insights/CombatTab';
import TrendTab from '@/components/insights/TrendTab';
import { RosterTabs } from './Roster';

// R4+ statistics hub (Plans 050–056). One tab per topic, kept in ?tab=.
const TABS = [
  { key: 'weekly', label: 'Weekly', render: () => <WeeklyTab /> },
  { key: 'reporting', label: 'Reporting', render: () => <ReportingTab /> },
  { key: 'growth', label: 'Growth by class', render: () => <GrowthTab /> },
  { key: 'vs', label: 'VS vs power', render: () => <VsPowerTab /> },
  { key: 'combat', label: 'Combat efficiency', render: () => <CombatTab /> },
  { key: 'troops', label: 'Troop mix', render: () => <TroopMixTab /> },
  { key: 'trend', label: 'Alliance trend', render: () => <TrendTab /> },
] as const;

type TabKey = (typeof TABS)[number]['key'];

export default function AdminInsights({ user }: { user: AuthUser }) {
  const canEdit = user.role === 'R5' || user.role === 'R4';
  const [params, setParams] = useSearchParams();
  const active: TabKey =
    (TABS.find((t) => t.key === params.get('tab'))?.key as TabKey | undefined) ?? TABS[0].key;

  return (
    <div className="space-y-4">
      <RosterTabs />
      <h1 className="text-xl font-semibold">Insights</h1>
      {!canEdit ? (
        <p className="rounded border border-slate-800 bg-slate-900/40 p-3 text-sm text-slate-400">
          R4/R5 required.
        </p>
      ) : (
        <>
          <div className="inline-flex max-w-full overflow-x-auto rounded-lg border border-slate-800 bg-slate-900 p-0.5 text-sm">
            {TABS.map((t) => (
              <button
                key={t.key}
                type="button"
                onClick={() => setParams({ tab: t.key }, { replace: true })}
                className={cn(
                  'whitespace-nowrap rounded px-3 py-1.5 transition',
                  active === t.key ? 'bg-slate-700 text-white' : 'text-slate-400 hover:text-white',
                )}
              >
                {t.label}
              </button>
            ))}
          </div>
          {TABS.find((t) => t.key === active)!.render()}
        </>
      )}
    </div>
  );
}
